import React, { useState } from 'react';
import styled from 'styled-components';

import { BUTTON_THEMES, CARD_TYPES } from '../../../shared/constants';
import { IContentfulLinkWithModal } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, ReactMarkdownExtended } from '../../../utils/generic';
import { languageHelper } from '../../../utils/language';
import { LABELS } from '../../../utils/language/filter-labels';

import mq from '../../../styles/breakpoints';
import buttonStyles, { CtaWrapper } from '../../../styles/buttonStyles';
import colors from '../../../styles/colors';
import { Body, H2, Label } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import Modal from '../../organisms/modal/Modal';
import { SimpleContent } from '../simple-content/SimpleContent';

import { ContentHighlightProps } from './ContentHighlight.types';

const ContentHighlight = ({
  id,
  index,
  title,
  subtitle,
  image,
  primaryLink,
  secondaryLink,
  children,
}: ContentHighlightProps) => {
  const { lang } = languageHelper();

  const [showModal, setShowModalHandler] = useState(false);
  const [activeCtaLink, setActiveCtaLink] =
    useState<IContentfulLinkWithModal>();

  const handleOpenModal = (link: IContentfulLinkWithModal) => {
    setShowModalHandler(true);
    setActiveCtaLink(link);
  };

  const ctaPrimaryLink =
    primaryLink && !primaryLink.dialogueModal
      ? getCtaLink(primaryLink)
      : undefined;

  const ctaSecondaryLink =
    secondaryLink && !secondaryLink.dialogueModal
      ? getCtaLink(secondaryLink)
      : undefined;

  const renderButtons = () => (
    <StyledCtaWrapper>
      {primaryLink && (
        <StyledPrimaryButton
          showModal={!!primaryLink?.dialogueModal}
          onButtonClick={() =>
            primaryLink.dialogueModal && handleOpenModal(primaryLink)
          }
          type={primaryLink?.type}
          label={primaryLink.label || primaryLink.name}
          buttonTheme={primaryLink.theme || BUTTON_THEMES.default}
          to={ctaPrimaryLink as string}
          iconPosition={primaryLink?.iconPosition}
          icon={primaryLink?.icon}
          deqComponentTitle={title || subtitle}
          deqCustomAttributes={{
            type: DEQ_TYPES.textLink,
          }}
        />
      )}
      {secondaryLink && (
        <StyledSecondaryButton
          showModal={!!secondaryLink?.dialogueModal}
          onButtonClick={() =>
            secondaryLink.dialogueModal && handleOpenModal(secondaryLink)
          }
          type={secondaryLink?.type}
          label={secondaryLink.label || secondaryLink.name}
          buttonTheme={secondaryLink.theme || BUTTON_THEMES.default}
          to={ctaSecondaryLink as string}
          iconPosition={secondaryLink?.iconPosition}
          icon={secondaryLink?.icon}
          deqComponentTitle={title || subtitle}
          deqCustomAttributes={{
            type: DEQ_TYPES.textLink,
          }}
        />
      )}
    </StyledCtaWrapper>
  );

  return (
    <>
      <Card
        id={id || CARD_TYPES.contentHighlight}
        key={title || subtitle || `${index}`}
      >
        {image?.file && (
          <ImageContainer>
            <img data-testid="image" alt="" src={image.file?.url} />
          </ImageContainer>
        )}
        <TextContainer>
          {subtitle && <StyledLabel>{subtitle}</StyledLabel>}
          {title && <StyledH2>{title}</StyledH2>}
          <StyledBody>{children}</StyledBody>
          {(primaryLink || secondaryLink) && renderButtons()}
        </TextContainer>
      </Card>
      <Modal
        show={showModal}
        onRequestCloseHandler={() => setShowModalHandler(!showModal)}
      >
        {activeCtaLink?.dialogueModal && (
          <SimpleContent
            setShowModal={(event) => {
              setShowModalHandler(event);
            }}
            title={activeCtaLink.dialogueModal.title}
            buttonLabel={LABELS.close[lang]}
            primaryLink={activeCtaLink?.dialogueModal.primaryLink}
          >
            <ReactMarkdownExtended componentType={CARD_TYPES.contentHighlight}>
              {
                activeCtaLink.dialogueModal.body.childMarkdownRemark
                  .rawMarkdownBody
              }
            </ReactMarkdownExtended>
          </SimpleContent>
        )}
      </Modal>
    </>
  );
};

export default ContentHighlight;

const Card = styled.div`
  background-color: ${colors.white};
  border-radius: ${({ theme }) => theme.radiuses.M};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.M};
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    width: 100%;
    max-width: auto;
    margin-lef: auto;
    margin-right: auto;
    justify-content: space-between;
    flex-direction: row;
    padding: ${Spacings.L};
  `}

  ${mq.from.breakpoint.L`
    max-width: 810px;
    width: auto;
    margin-left: auto;
    margin-right: auto;
    padding: ${Spacings.XL};
  `}

  ${mq.from.breakpoint.XL`
    max-width: 840px;
  `}
`;

const StyledH2 = styled(H2)`
  margin-bottom: ${Spacings.XS};
`;

const TextContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  margin-top: 0;
`;

const ImageContainer = styled.div`
  height: 72px;
  width: 72px;

  img {
    height: inherit;
    position: relative;
    width: inherit;
  }

  ${mq.from.breakpoint.M`
    align-self: center;
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyles.primaryButtonPosition}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;

const StyledBody = styled(Body)`
  h1 {
    strong {
      color: ${({ theme }) => theme.colors.abnGreen500};
      font-family: ${({ theme }) => theme.fontBold};
    }
  }

  h1,
  h2 {
    margin-bottom: ${Spacings.XS};
    margin-top: 0;
  }
`;

const StyledLabel = styled(Label)`
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: ${Spacings.XS};
`;

const StyledCtaWrapper = styled(CtaWrapper)`
  column-gap: ${Spacings.L};
  margin-top: ${Spacings.M};
  row-gap: ${Spacings.S};

  > a {
    width: 100%;
  }

  ${mq.from.breakpoint.M`
    flex-wrap: wrap;
    flex-direction: initial;
    align-items: center;

    > a {
      width: auto;
    }
  `}
`;
